import type { ReactNode } from 'react';
import { clsx } from 'clsx';

export function Spinner({
  className,
  width = 18,
}: {
  className?: string;
  width?: number;
}) {
  return (
    <svg
      className={clsx('animate-spin', className)}
      width={width}
      height={width}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="2.5"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function LoadingMessage({ message }: { message: string }) {
  return (
    <div className="flex flex-1 items-center justify-center gap-2 p-10 text-md text-page-text-light">
      <Spinner />
      {message}
    </div>
  );
}

/**
 * Placeholder rows shaped like the table that is about to appear, so the
 * layout does not jump once data lands. The shimmer itself lives in
 * style/polish.css under the `data-polish` switch.
 */
export function TableSkeleton({
  rows = 8,
  columns = 6,
  label,
}: {
  rows?: number;
  columns?: number;
  label: string;
}) {
  return (
    <div
      className="flex-1 overflow-hidden p-2"
      role="status"
      aria-live="polite"
      aria-label={label}
      data-testid="table-skeleton"
    >
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          className="mb-1.5 grid gap-2"
          style={{
            gridTemplateColumns: `1.4fr repeat(${columns - 1}, 1fr)`,
          }}
        >
          {Array.from({ length: columns }).map((__, colIndex) => (
            <div
              key={colIndex}
              className="skeleton h-5"
              style={{ opacity: 1 - rowIndex * 0.07 }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Empty state that names the next action instead of just reporting nothing. */
export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint: string;
  action?: ReactNode;
}) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-1.5 p-12 text-center"
      data-testid="empty-state"
    >
      <p className="text-md font-medium text-page-text">{title}</p>
      <p className="max-w-sm text-sm text-page-text-light">{hint}</p>
      {action && <div className="pt-2">{action}</div>}
    </div>
  );
}
